import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/base/Breadcrumb";
import MediaCard from "@/components/base/MediaCard";
import EmptyState from "@/components/base/EmptyState";
import { getMediaById, getRelatedMedia } from "@/lib/media";
import { MEDIA_TYPE_LABEL, MEDIA_TYPE_PATH } from "@/types/media";
import { useFavorites } from "@/hooks/useFavorites";

export default function DetailPage() {
  const { id } = useParams();
  const item = id ? getMediaById(id) : undefined;
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState<string | null>(null);

  const related = useMemo(() => (item ? getRelatedMedia(item, 4) : []), [item]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 2000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  usePageMeta({
    title: item ? `${item.title} - 91蜜桃` : "作品详情 - 91蜜桃",
    description: item
      ? `${item.title}（${item.year}）${item.category}作品简介：${item.description.slice(0, 80)}`
      : "91蜜桃影视作品详情页。",
    keywords: item
      ? `91蜜桃,${item.title},${item.category},${item.region},${item.director},影视资源`
      : "91蜜桃,影视资源",
  });

  if (!item) {
    return (
      <div className="mx-auto max-w-[1280px] px-4 py-20 md:px-8">
        <EmptyState
          icon="ri-film-line"
          title="抱歉，没有找到这部作品"
          desc="作品可能已下架或链接有误，去看看其他推荐吧。"
          backHome
        />
      </div>
    );
  }

  const favorited = isFavorite(item.id);
  const typeLabel = MEDIA_TYPE_LABEL[item.type];

  const handleFavorite = () => {
    const now = toggleFavorite(item.id);
    setToast(now ? "已加入收藏" : "已取消收藏");
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setToast("页面链接已复制");
    } catch {
      setToast("复制失败，请手动复制地址栏链接");
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-6 md:px-8 md:py-7">
          <Breadcrumb
            items={[
              { label: typeLabel, to: `/category/${MEDIA_TYPE_PATH[item.type]}` },
              { label: item.title },
            ]}
          />
        </div>
      </div>

      <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[300px_1fr] lg:gap-10">
          <div className="mx-auto w-full max-w-[280px] lg:mx-0 lg:max-w-none">
            <div className="overflow-hidden rounded-lg bg-background-200 shadow-sm">
              <img
                src={item.cover}
                alt={`${item.title} 海报`}
                title={`${item.title} - 91蜜桃`}
                className="aspect-[2/3] w-full object-cover object-top"
              />
            </div>
            <div className="mt-4 flex gap-3">
              <button
                type="button"
                onClick={handleFavorite}
                className={`flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-medium transition-colors ${
                  favorited
                    ? "bg-accent-500 text-secondary-950"
                    : "bg-primary-500 text-background-50 hover:bg-primary-600"
                }`}
              >
                <i className={favorited ? "ri-bookmark-fill" : "ri-bookmark-line"} />
                {favorited ? "已收藏" : "收藏作品"}
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
              >
                <i className="ri-share-forward-line" />
                分享
              </button>
            </div>
          </div>

          <div>
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to={`/category/${MEDIA_TYPE_PATH[item.type]}`}
                className="rounded-md bg-primary-50 px-2.5 py-1 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-100"
              >
                {typeLabel}
              </Link>
              <span className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-800">
                {item.category}
              </span>
              <span className="rounded-md bg-background-200 px-2.5 py-1 text-xs font-medium text-foreground-600">
                {item.region}
              </span>
            </div>

            <h1 className="mt-3 font-heading text-2xl font-black tracking-tight text-foreground-950 md:text-4xl">
              {item.title}
            </h1>

            <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-foreground-600">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line text-primary-600" />
                {item.year} 年
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-star-fill text-accent-500" />
                评分 <span className="font-bold text-foreground-900">{item.rating}</span>
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-user-star-line text-primary-600" />
                导演 {item.director}
              </span>
            </div>

            <div className="mt-5 rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-900">剧情简介</h2>
              <p className="mt-2.5 text-sm leading-7 text-foreground-700">{item.description}</p>
            </div>

            <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <p className="text-xs text-foreground-400">主要演员</p>
                <p className="mt-1 text-sm font-medium text-foreground-900">
                  {item.actors.join(" / ")}
                </p>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <p className="text-xs text-foreground-400">作品标签</p>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {item.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full bg-secondary-100 px-2 py-0.5 text-xs text-secondary-800"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-5 flex items-start gap-3 rounded-lg border border-accent-300/50 bg-accent-50 p-4">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-500 text-secondary-950">
                <i className="ri-information-line" />
              </span>
              <div>
                <p className="text-sm font-medium text-foreground-900">观看渠道提醒</p>
                <p className="mt-0.5 text-sm text-foreground-600">
                  本站暂不提供在线播放与下载，仅展示作品资料与资讯信息，请在正版平台观看。
                </p>
              </div>
            </div>
          </div>
        </div>

        <section className="mt-14">
          <div className="mb-6 flex items-end justify-between">
            <h2 className="font-heading text-xl font-black tracking-tight text-foreground-950 md:text-2xl">
              相关推荐
            </h2>
            <Link
              to={`/category/${MEDIA_TYPE_PATH[item.type]}`}
              className="flex items-center gap-1 whitespace-nowrap text-sm font-medium text-foreground-600 transition-colors hover:text-primary-600"
            >
              更多{typeLabel}
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 md:gap-5">
            {related.map((media, index) => (
              <MediaCard key={media.id} item={media} index={index} />
            ))}
          </div>
        </section>
      </div>

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-50 -translate-x-1/2 animate-fade-up rounded-full bg-secondary-950 px-5 py-2.5 text-sm text-background-50 shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}